import * as React from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import TableSortLabel from "@mui/material/TableSortLabel";
import { PrizeDrawParticipantReturn } from "../../../graphql/types";
import { useEnumStrings } from "../../enumStrings";

type Order = "asc" | "desc";

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string }
) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array: any[], comparator: (a: any, b: any) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [any, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headerCellStyle = {
  backgroundColor: "#2C3E50",
  color: "#FFFFFF",
  fontFamily: "Montserrat, sans-serif",
  fontWeight: 700,
  fontSize: "14px",
  lineHeight: "17px",
  borderBottom: "none",
  padding: "12px 16px",
  "@media (max-width: 600px)": {
    fontSize: "12px",
    padding: "10px 8px",
    lineHeight: "15px",
  },
};

const sortLabelStyle = {
  color: "#FFFFFF",
  fontSize: "14px",
  fontWeight: 700,
  "&.MuiTableSortLabel-root": {
    color: "#FFFFFF",
  },
  "&.MuiTableSortLabel-root:hover": {
    color: "#FFFFFF",
  },
  "&.Mui-active": {
    color: "#FFFFFF",
  },
  "& .MuiTableSortLabel-icon": {
    color: "#FFFFFF !important",
  },
};

const bodyCellStyle = {
  fontFamily: "Montserrat, sans-serif",
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: "17px",
  color: "#000000",
  padding: "12px 16px",
  borderBottom: "1px solid #E0E0E0",
  wordBreak: "break-word" as const,
  "@media (max-width: 600px)": {
    fontSize: "12px",
    padding: "10px 8px",
    lineHeight: "15px",
  },
};

export default function ParticipantTable({
  data,
}: {
  data: PrizeDrawParticipantReturn[];
}) {
  const enumStrings = useEnumStrings();
  const [order, setOrder] = React.useState<Order>("desc");
  const [orderBy, setOrderBy] =
    React.useState<keyof PrizeDrawParticipantReturn>("ballotsEarned");

  const handleRequestSort = (property: keyof PrizeDrawParticipantReturn) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedData = stableSort(data, getComparator(order, orderBy));

  return (
    <Box sx={{ width: "100%", margin: "auto" }}>
      <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: "none", border: "1px solid #E0E0E0" }}>
        <TableContainer sx={{ overflowX: "hidden" }}>
          <Table sx={{ tableLayout: "fixed", width: "100%" }}>
            <TableHead>
              <TableRow>
                <TableCell
                  align="center"
                  sx={{ ...headerCellStyle, width: "70%" }}
                >
                  <TableSortLabel
                    active={orderBy === "email"}
                    direction={orderBy === "email" ? order : "asc"}
                    onClick={() => handleRequestSort("email")}
                    sx={sortLabelStyle}
                  >
                    {enumStrings.rewardsPage.participantEmail}
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ ...headerCellStyle, width: "30%" }}
                >
                  <TableSortLabel
                    active={orderBy === "ballotsEarned"}
                    direction={orderBy === "ballotsEarned" ? order : "asc"}
                    onClick={() => handleRequestSort("ballotsEarned")}
                    sx={sortLabelStyle}
                  >
                    {enumStrings.rewardsPage.pointsEarned}
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedData.map((user, index) => (
                <TableRow key={index}>
                  <TableCell align="center" sx={{ ...bodyCellStyle, width: "70%" }}>
                    {user.email}
                  </TableCell>
                  <TableCell align="center" sx={{ ...bodyCellStyle, width: "30%" }}>
                    {user.ballotsEarned}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
